import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';

import { SimpleButton, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import StudentStatusReportDataTable from '@/src/pages/reports/statusReport/components/StudentStatusReportDataTable';
import type { AssignmentStudentDetailDTO } from '@/src/pages/reports/statusReport/models/statusReportDTOs';

interface StatusReportModalProps {
    examAssignmentId: number;
    examName: string;
    classIds: number[];
    open: boolean;
    onClose: () => void;
}

export default function StatusReportModal({
    examAssignmentId,
    examName,
    classIds,
    open,
    onClose,
}: StatusReportModalProps) {
    const [data, setData] = useState<Array<AssignmentStudentDetailDTO>>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [pendingStudentIds, setPendingStudentIds] = useState<Set<number>>(new Set());
    const [classFreezeLoading, setClassFreezeLoading] = useState(false);

    const allFrozen = useMemo(
        () => data.length > 0 && data.every((r) => r.isTeacherFrozen === true),
        [data]
    );
    const classFreezeLabel = classFreezeLoading
        ? 'Loading...'
        : allFrozen
          ? 'Unfreeze All'
          : 'Freeze All';
    const nextClassFrozen = !allFrozen;

    const fetchData = useCallback(async () => {
        setLoaded(false);
        setError('');

        try {
            if (classIds.length === 0) {
                setData([]);
                return;
            }

            const requests = classIds.map((classId) =>
                apiRequest<Array<AssignmentStudentDetailDTO>>(
                    'GET',
                    `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}&classId=${classId}`
                )
            );

            const results = await Promise.all(requests);
            const combined = results.flatMap((r) => r.data ?? []);
            setData(combined);
        } catch {
            setError('Failed to load status report details.');
        } finally {
            setLoaded(true);
        }
    }, [examAssignmentId, classIds]);

    async function handleToggleFreeze(
        studentUserId: number,
        isFrozen: boolean,
        attemptExamAssignmentId?: number | null
    ) {
        const target = isFrozen;
        const assignmentId = attemptExamAssignmentId ?? examAssignmentId;

        setPendingStudentIds((prev) => new Set(prev).add(studentUserId));
        try {
            await apiRequest('POST', 'reports/statusReport/freeze/student', {
                examAssignmentId: assignmentId,
                studentUserId,
                isFrozen: target,
            });

            const verifyResults = await Promise.all(
                classIds.map((classId) =>
                    apiRequest<Array<AssignmentStudentDetailDTO>>(
                        'GET',
                        `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}&classId=${classId}`
                    )
                )
            );
            const fresh = verifyResults.flatMap((r) => r.data ?? []);
            const serverRow = fresh.find((r) => r.studentUserId === studentUserId);
            if (serverRow) {
                setData((prev) =>
                    prev.map((r) =>
                        r.studentUserId === studentUserId
                            ? { ...r, isTeacherFrozen: !!serverRow.isTeacherFrozen }
                            : r
                    )
                );
            }
        } catch {
            setError('Failed to toggle freeze for student.');
        } finally {
            setPendingStudentIds((prev) => {
                const next = new Set(prev);
                next.delete(studentUserId);
                return next;
            });
        }
    }

    async function handleToggleFreezeAll() {
        setClassFreezeLoading(true);
        try {
            await apiRequest('POST', 'reports/statusReport/freeze', {
                examAssignmentId,
                isFrozen: nextClassFrozen,
            });

            const verifyResults = await Promise.all(
                classIds.map((classId) =>
                    apiRequest<Array<AssignmentStudentDetailDTO>>(
                        'GET',
                        `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}&classId=${classId}`
                    )
                )
            );
            setData(verifyResults.flatMap((r) => r.data ?? []));
        } catch {
            setError('Failed to toggle freeze for all students.');
        } finally {
            setClassFreezeLoading(false);
        }
    }

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open, fetchData]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    width: '85%',
                    height: '85%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Status Report — {examName}
                </Typography>

                <Loading visible={!loaded} message="Loading status report..." />
                <ErrorMessage error={error} style={{ marginBottom: '8px' }} />

                {loaded && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {data.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <SimpleButton
                                    onClick={handleToggleFreezeAll}
                                    variant="contained"
                                    color="primary"
                                    disabled={classFreezeLoading}
                                    aria-pressed={allFrozen}
                                    aria-label={classFreezeLabel}
                                >
                                    {classFreezeLabel}
                                </SimpleButton>
                            </Box>
                        )}

                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <StudentStatusReportDataTable
                                data={data}
                                searchString=""
                                onToggleFreeze={handleToggleFreeze}
                                pendingStudentIds={pendingStudentIds}
                            />
                        </Box>
                    </Box>
                )}

                <Box textAlign="right" mt={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
